import React, { useMemo, useState, useCallback } from 'react'
import Skeleton from 'react-loading-skeleton'

import { ParentSlug, TimeBasedRules } from 'src/apollo/onlineOrdering'
import { Product, ProductVariant } from '../hooks/useProductVariants'

import FormattedPrice from 'shared/components/common/price/FormattedPrice'
import PriceFromRange from 'shared/components/common/price/PriceFromRange'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'
import { t } from 'src/translations'

import { CollapsibleText } from 'public/components/default_template/collapsible_text/CollapsibleText'
import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import {
  useItemTags,
  ItemTags
} from 'public/components/default_template/online_ordering/item_tags/ItemTags'
import { TimeBasedRuleMenuItemTag } from 'public/components/default_template/online_ordering/item_tags/MenuItemTags'
import Breadcrumbs from 'public/components/default_template/product_display/ecommerce_product_display/navigation/Breadcrumbs'
import { MenuItemImage } from 'public/components/default_template/product_display/MenuItemImage'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import { useOffers } from 'public/components/online_ordering/OffersContext'
import { ModifierGroup } from 'public/components/online_ordering/types'

import { AddToCartSection } from './cart/AddToCartSection'
import { ItemModifiersSection } from './modifiers/ItemModifiersSection'
import { StockSection } from './stock/StockSection'
import { ProductDisplayNestedModifierModal } from 'public/components/default_template/product_display/ProductDisplayNestedModifierModal'
import {
  VariationModifiersSection,
  VariationOptionType,
  SelectedOption
} from './modifiers/VariationModifiersSection'
import { SingleValue } from 'react-select'
import { ProductImageGallery } from './gallery/ProductImageGallery'
import {
  findBestMatchingVariant,
  findExactMatchingVariants,
  findPrimarySelection,
  findVariantWithImagesForOption,
  resolveImages
} from './utils/productVariantUtils'

type Props = {
  menuItem?: MenuItem
  modifierGroups: ModifierGroup[]
  setShowFooter: (showFooter: boolean) => void
  loading: boolean
  isAvailableNow: boolean
  variationSlug?: string
  product?: Product
  onVariationSelect?: (variation: ProductVariant | null) => void
}

export const ProductDisplay = ({
  menuItem,
  modifierGroups,
  setShowFooter,
  loading,
  isAvailableNow,
  product,
  onVariationSelect
}: Props) => {
  const { price, quantity, invalidRequiredGroups, modifiers } =
    useModifierContext()

  // Show modal when user has selected a nested modifier (level 2+)
  const showNestedModal = modifiers.length > 1
  const [selectedVariationOptions, setSelectedVariationOptions] = useState<
    SelectedOption[]
  >([])
  const [displayedVariant, setDisplayedVariant] =
    useState<ProductVariant | null>(null)
  const [variationOptionsInvalid, setVariationOptionsInvalid] = useState(false)

  // If availableOptions are provided, render variation option selectors instead of modifier groups
  const hasAvailableOptions =
    product?.availableOptions && product.availableOptions.length > 0

  // Track which variation options are missing selections
  const unselectedVariationOptions = useMemo(() => {
    if (!hasAvailableOptions) return []

    const selectedOptionIds = new Set(
      selectedVariationOptions.map((opt) => opt.optionId)
    )
    return product.availableOptions.filter(
      (option) => !selectedOptionIds.has(option.id)
    )
  }, [hasAvailableOptions, product?.availableOptions, selectedVariationOptions])

  // Check if all variation options have been selected
  const hasUnselectedVariationOptions = unselectedVariationOptions.length > 0

  // Primary option is the first by sortOrder — used for image fallback and best-match tiebreaking
  const primaryOptionId = useMemo(
    () =>
      [...(product?.availableOptions ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder
      )[0]?.id,
    [product?.availableOptions]
  )

  const primarySelection = useMemo(
    () => findPrimarySelection(selectedVariationOptions, primaryOptionId),
    [selectedVariationOptions, primaryOptionId]
  )

  // Consolidated display properties with fallback logic
  // Priority: menuItem → displayedVariant → product/firstVariant
  const displayItem: MenuItem = useMemo(() => {
    // If menuItem exists, use it directly (takes precedence over everything)
    // Apply imageUrls fallback even for menuItem
    if (menuItem) {
      return {
        ...menuItem,
        imageUrls: menuItem.imageUrls ?? menuItem.imageSetUrls?.[0]
      }
    }

    const firstVariant = product?.variants?.[0]

    // Resolve images: exact variant → first option set fallback → product → firstVariant
    // resolveImages skips null/undefined sources automatically
    const primaryOptionFallback = primarySelection
      ? findVariantWithImagesForOption(
          product?.variants ?? [],
          primarySelection
        )
      : null
    const { imageUrls: imageUrlsToUse, imageSetUrls: imageSetUrlsToUse } =
      resolveImages([
        displayedVariant,
        primaryOptionFallback,
        product,
        firstVariant
      ])

    return {
      name: product?.name ?? null,
      description:
        displayedVariant?.description ?? product?.description ?? null,
      guid: displayedVariant?.guid ?? firstVariant?.guid ?? null,
      itemGroupGuid:
        displayedVariant?.itemGroupGuid ?? product?.itemGroupGuid ?? '',
      itemTags: displayedVariant?.itemTags ?? product?.itemTags,
      imageUrls: imageUrlsToUse,
      imageSetUrls: imageSetUrlsToUse,
      contentAdvisories:
        displayedVariant?.contentAdvisories ?? firstVariant?.contentAdvisories,
      parentSlugs: displayedVariant?.parentSlugs ?? firstVariant?.parentSlugs,
      timeBasedRules:
        displayedVariant?.timeBasedRules ?? firstVariant?.timeBasedRules,
      outOfStock: displayedVariant?.outOfStock ?? product?.outOfStock ?? null,
      stockAcrossLocations:
        displayedVariant?.stockAcrossLocations ??
        firstVariant?.stockAcrossLocations
    }
  }, [menuItem, product, displayedVariant, primarySelection])

  // Include default modifier prices in total calculation
  const totalItemPrice = useMemo(() => {
    // If all variation options are selected, use the displayedVariant's specific price
    if (
      hasAvailableOptions &&
      !hasUnselectedVariationOptions &&
      displayedVariant
    ) {
      const variantPrices = (displayedVariant as MenuItem).prices
      if (variantPrices && variantPrices.length > 0) {
        return quantity * variantPrices[0]!
      }
    }

    // Otherwise, use product price (for products without a selected menuItem)
    if (product?.prices && product.prices.length > 0) {
      // Use the first price (or could show range if needed)
      return quantity * product.prices[0]!
    }

    // If menuItem exists, use its price from ModifierContext
    if (menuItem) {
      const basePrice = quantity * price
      const defaultModifiersPrices = menuItem.defaultModifiersPrice ?? 0
      return basePrice + defaultModifiersPrices
    }

    return 0
  }, [
    quantity,
    price,
    menuItem,
    product?.prices,
    hasAvailableOptions,
    hasUnselectedVariationOptions,
    displayedVariant
  ])

  // Check if item has required modifiers that haven't been selected yet
  const hasUnselectedRequiredModifiers = useMemo(() => {
    return invalidRequiredGroups.length > 0
  }, [invalidRequiredGroups])

  // Determine what to display for price
  const priceDisplay = useMemo(() => {
    if (isNaN(quantity)) {
      return '0.00'
    }

    // If there are unselected required modifiers and total price is 0, show message instead
    if (hasUnselectedRequiredModifiers && totalItemPrice === 0) {
      return t('product-display.price-calculated-after-modifier-selection')
    }

    // Check if all variation options are selected (we have an exact variant)
    const allVariationOptionsSelected =
      hasAvailableOptions && !hasUnselectedVariationOptions

    // For products with multiple price points and no menuItem/variant selected, show range
    // BUT: don't show range if all variation options are selected (we have an exact variant)
    if (
      !menuItem &&
      !allVariationOptionsSelected &&
      product?.prices &&
      product.prices.length > 1
    ) {
      return (
        <PriceFromRange
          price={null}
          prices={product.prices.map((p) => p * quantity)}
        />
      )
    }

    // Use totalItemPrice for all other cases (includes variant price when all options selected)
    return <FormattedPrice value={totalItemPrice} />
  }, [
    quantity,
    hasUnselectedRequiredModifiers,
    totalItemPrice,
    menuItem,
    product?.prices,
    hasAvailableOptions,
    hasUnselectedVariationOptions
  ])

  const { getOffersTextForEntity } = useOffers()
  const offersText = getOffersTextForEntity(
    displayItem.guid,
    displayItem.itemGroupGuid
  )
  // eslint-disable-next-line no-extra-parens
  const itemTags = useItemTags(
    displayItem.itemTags,
    displayItem.contentAdvisories ?? undefined
  )
  const { dotFoodWasteSecondarySales, ooMultiImageGallery } = useFlags()
  const displayableTimeBasedRules = useMemo((): TimeBasedRules | undefined => {
    const rules = displayItem.timeBasedRules
    if (!rules) return undefined
    const filtered = { ...rules }
    if (!dotFoodWasteSecondarySales) {
      delete filtered.pickupWindowRule
    }
    if (
      filtered.leadTimeRule ||
      filtered.preorderRule ||
      filtered.pickupWindowRule
    ) {
      return filtered
    }
    return undefined
  }, [displayItem.timeBasedRules, dotFoodWasteSecondarySales])

  const onChange = useCallback(
    (selection: SingleValue<VariationOptionType>, optionId: string) => {
      if (!selection?.value?.id) {
        return
      }

      // Use functional setState to avoid stale closure issues
      setSelectedVariationOptions((prevSelectedOptions) => {
        // Remove any existing selection from the same option, then add new one
        const newSelectedVariationOptions = [
          ...prevSelectedOptions.filter((opt) => opt.optionId !== optionId),
          { optionId, valueId: selection.value.id }
        ]

        // If no selections, reset to base product
        if (newSelectedVariationOptions.length === 0) {
          setDisplayedVariant(null)
          onVariationSelect?.(null)
          return newSelectedVariationOptions
        }

        // Find exact matches (variants that match ALL selected options)
        const exactMatches = findExactMatchingVariants(
          product?.variants ?? [],
          newSelectedVariationOptions
        )

        // Use findBestMatchingVariant for display — it picks the variant with
        // the most matching options, preferring the primary option and images.
        // Must resolve primarySelection from newSelectedVariationOptions (not the
        // memoized value) since we're inside the setState updater.
        const bestMatch = findBestMatchingVariant(
          product?.variants ?? [],
          newSelectedVariationOptions,
          findPrimarySelection(newSelectedVariationOptions, primaryOptionId)
        )
        setDisplayedVariant(bestMatch)

        // Notify parent of variant selection: exact match when one variant
        // matches all options, null otherwise to clear stale selection
        if (exactMatches.length === 1 && exactMatches[0]) {
          onVariationSelect?.(exactMatches[0])
        } else {
          onVariationSelect?.(null)
        }

        return newSelectedVariationOptions
      })
    },
    [product, onVariationSelect, primaryOptionId]
  )

  if (loading) {
    return <LoadingSkeleton />
  }

  return (
    <div className='paddedContent'>
      <div
        className='productDisplayBreadcrumbs'
        aria-label={t('product-display.breadcrumbs')}
      >
        <Breadcrumbs
          name={displayItem.name ?? ''}
          parentSlugs={
            displayItem.parentSlugs as ParentSlug[] | null | undefined
          }
        />
      </div>
      <div className='productDisplaySection'>
        {ooMultiImageGallery &&
        displayItem?.imageSetUrls &&
        displayItem.imageSetUrls.length > 1 ? (
          <div className='productImageSection'>
            <ProductImageGallery
              imageSetUrls={displayItem.imageSetUrls}
              itemName={displayItem.name ?? ''}
            />
          </div>
        ) : (
          displayItem?.imageUrls && (
            <div className='productImageSection'>
              <MenuItemImage item={displayItem} />
            </div>
          )
        )}
        <div className='productInfoSection'>
          <h2 className='productNameRow' data-testid='productName'>
            {displayItem.name}
          </h2>

          {isAvailableNow && (
            <>
              <div className='pricesRow'>
                {/* TODO show offer price */}
                <h4>{priceDisplay}</h4>
                <div className='productDisplayTags'>
                  {displayableTimeBasedRules && (
                    <TimeBasedRuleMenuItemTag
                      timeBasedRules={displayableTimeBasedRules}
                      testId={
                        'time-based-ordering-tag-pdp-' +
                        (displayItem.guid ?? '')
                      }
                    />
                  )}
                  <ItemTags offersText={offersText} itemTags={itemTags} />
                </div>
              </div>

              <div className='ecommPdpModifiers'>
                {hasAvailableOptions && (
                  <VariationModifiersSection
                    product={product}
                    selectedOptions={selectedVariationOptions}
                    onChange={onChange}
                    variationOptionsInvalid={variationOptionsInvalid}
                    unselectedOptions={unselectedVariationOptions}
                  />
                )}

                {!hasAvailableOptions && (
                  <ItemModifiersSection groups={modifierGroups} />
                )}
              </div>

              <StockSection menuItem={displayItem} />

              <AddToCartSection
                totalItemPrice={totalItemPrice}
                setShowFooter={setShowFooter}
                outOfStock={displayItem.outOfStock ?? false}
                hasVariationOptions={hasAvailableOptions}
                hasUnselectedVariationOptions={hasUnselectedVariationOptions}
                setVariationOptionsInvalid={setVariationOptionsInvalid}
                unselectedVariationOptions={unselectedVariationOptions}
              />
            </>
          )}

          <CollapsibleText
            text={displayItem.description ?? ''}
            className='productDescription'
          />
        </div>
      </div>

      {showNestedModal && <ProductDisplayNestedModifierModal />}
    </div>
  )
}

const LoadingSkeleton = () => {
  const isMobile = useIsMobile(ScreenWidth.SMALL)

  return (
    <div className='paddedContent'>
      <div
        className='productDisplayBreadcrumbs'
        aria-label={t('product-display.breadcrumbs')}
      >
        <Skeleton width={200} />
      </div>
      <div className='productDisplaySection'>
        <div className='productImageSection'>
          <Skeleton
            containerClassName='loadingSkeleton'
            height={isMobile ? 300 : 600}
          />
        </div>
        <div className='productInfoSection'>
          <h2 className='productNameRow' data-testid='productName'>
            <Skeleton containerTestId='productName-loading-skeleton' />
          </h2>

          <div className='pricesRow'>
            <Skeleton
              width='50%'
              containerClassName='loadingSkeleton'
              containerTestId='price-loading-skeleton'
            />
          </div>

          <div className='stockRow'>
            <Skeleton
              width='40%'
              containerClassName='loadingSkeleton'
              containerTestId='stock-loading-skeleton'
            />
          </div>

          <div className='otherLocationsStockRow'>
            <Skeleton
              width='50%'
              containerClassName='loadingSkeleton'
              containerTestId='location-stock-loading-skeleton'
            />
          </div>

          <div className='modifierGroupRow'>
            <Skeleton count={2} containerTestId='modifiers-loading-skeleton' />
          </div>

          <div className='addToCartRow'>
            <Skeleton containerClassName='loadingSkeleton' height={48} />
            <Skeleton width={100} height={48} />
          </div>

          <Skeleton count={2} containerTestId='description-loading-skeleton' />
        </div>
      </div>
    </div>
  )
}
